import React, { useState, useContext } from 'react';
import { DContext } from '../../../context/DataContext';
import { useTranslation } from 'react-i18next';
import UploadImg from '../../../assets/server-push.svg';
import { compressImages } from '../../../utils/compressImage';

function CreatePackage() {
  const { t } = useTranslation();
  const { BeURL, setLoader } = useContext(DContext);

  // Multi-language fields
  const [title, setTitle] = useState({ en: '', fr: '', ts: '' });
  const [description, setDescription] = useState({ en: '', fr: '', ts: '' });
  const [activities, setActivities] = useState({ en: '', fr: '', ts: '' });
  const [inclusion, setInclusion] = useState({ en: '', fr: '', ts: '' });
  const [whatToBring, setWhatToBring] = useState({ en: '', fr: '', ts: '' });
  const [destinations, setDestinations] = useState({ en: '', fr: '', ts: '' });

  // Price state for package type
  const [price, setPrice] = useState({
    thb: { kid: '', adult: '' },
    usd: { kid: '', adult: '' }
  });
  const [halfPrice, setHalfPrice] = useState({
    thb: { kid: '', adult: '' },
    usd: { kid: '', adult: '' }
  });

  // Price state for boat type
  const [speedBoatPrice, setSpeedBoatPrice] = useState({
    thb: { kid: '', adult: '' },
    usd: { kid: '', adult: '' }
  });
  const [longTailPrice, setLongTailPrice] = useState({
    thb: { kid: '', adult: '' },
    usd: { kid: '', adult: '' }
  });

  // Radio and checkboxes controlling pricing mode
  const [priceType, setPriceType] = useState("pkg"); // "pkg" or "boat"
  const [isHalfPrice, setIsHalfPrice] = useState(false);
  const [isLongTailPrice, setIsLongTailPrice] = useState(false);
  const [enableEntranceFees, setEnableEntranceFees] = useState(false);
  const [popular, setPopular] = useState(false);

  // Entrance fees value
  const [entranceFees, setEntranceFees] = useState({ thb: '', usd: '' });

  // Image states: four files and their previews
  const [images, setImages] = useState([null, null, null, null]);
  const [imagePreviews, setImagePreviews] = useState([UploadImg, UploadImg, UploadImg, UploadImg]);

  // Global error messages for mandatory fields
  const [errors, setErrors] = useState({});

  // Handler for onBlur for mandatory fields
  const handleBlur = (fieldKey, value) => {
    if (!value || value.trim() === '') {
      setErrors(prev => ({ ...prev, [fieldKey]: t('create-package.requiredField') }));
    } else {
      setErrors(prev => ({ ...prev, [fieldKey]: '' }));
    }
  };

  // Image file change handler per image field index
  const handleImageChange = (e, index) => {
    const file = e.target.files[0];
    if (!file) return;
    const newImages = [...images];
    newImages[index] = file;
    setImages(newImages);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const newPreviews = [...imagePreviews];
      newPreviews[index] = ev.target.result;
      setImagePreviews(newPreviews);
    };
    reader.readAsDataURL(file);
  };

  // Helper to parse CSV string into an array
  const parseCSV = (str) =>
    str.split(',').map(item => item.trim()).filter(item => item);

  // Validate mandatory fields on submission
  const validateAllFields = () => {
    // Check multi-language fields for title, description, activities, destinations
    for (let lang of ['en', 'fr', 'ts']) {
      if (!title[lang].trim()) return t('create-package.requiredField');
      if (!description[lang].trim()) return t('create-package.requiredField');
      if (!activities[lang].trim()) return t('create-package.requiredField');
      if (!destinations[lang].trim()) return t('create-package.requiredField');
    }
    // Validate pricing based on type
    if (priceType === 'pkg') {
      if (!price.thb.adult || isNaN(parseFloat(price.thb.adult))) return t('create-package.requiredField');
      if (!price.usd.adult || isNaN(parseFloat(price.usd.adult))) return t('create-package.requiredField');
      if (isHalfPrice) {
        if (!halfPrice.thb.adult || isNaN(parseFloat(halfPrice.thb.adult))) return t('create-package.requiredField');
        if (!halfPrice.usd.adult || isNaN(parseFloat(halfPrice.usd.adult))) return t('create-package.requiredField');
      }
    } else if (priceType === 'boat') {
      if (!speedBoatPrice.thb.adult || isNaN(parseFloat(speedBoatPrice.thb.adult))) return t('create-package.requiredField');
      if (!speedBoatPrice.usd.adult || isNaN(parseFloat(speedBoatPrice.usd.adult))) return t('create-package.requiredField');
      if (isLongTailPrice) {
        if (!longTailPrice.thb.adult || isNaN(parseFloat(longTailPrice.thb.adult))) return t('create-package.requiredField');
        if (!longTailPrice.usd.adult || isNaN(parseFloat(longTailPrice.usd.adult))) return t('create-package.requiredField');
      }
    }
    // Entrance fees if enabled must be provided.
    if (enableEntranceFees) {
      if (!entranceFees.thb.trim() || isNaN(parseFloat(entranceFees.thb)))
        return t('create-package.requiredField');
      if (!entranceFees.usd.trim() || isNaN(parseFloat(entranceFees.usd)))
        return t('create-package.requiredField');
    }
    // All 4 images must be selected.
    if (images.some(img => img === null)) return t('create-package.requiredField');
    return null;
  };

  // Form submission handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    const errorMsg = validateAllFields();
    if (errorMsg) {
      setErrors(prev => ({ ...prev, form: errorMsg }));
      return;
    }

    // Build FormData and attach JSON-stringified fields
    const formData = new FormData();
    formData.append('title', JSON.stringify(title));
    formData.append('description', JSON.stringify(description));
    formData.append('activities', JSON.stringify({
      en: parseCSV(activities.en),
      fr: parseCSV(activities.fr),
      ts: parseCSV(activities.ts)
    }));
    formData.append('inclusion', JSON.stringify({
      en: parseCSV(inclusion.en),
      fr: parseCSV(inclusion.fr),
      ts: parseCSV(inclusion.ts)
    }));
    formData.append('whatToBring', JSON.stringify({
      en: parseCSV(whatToBring.en),
      fr: parseCSV(whatToBring.fr),
      ts: parseCSV(whatToBring.ts)
    }));
    formData.append('destinations', JSON.stringify({
      en: parseCSV(destinations.en),
      fr: parseCSV(destinations.fr),
      ts: parseCSV(destinations.ts)
    }));

    // Append pricing based on priceType
    formData.append('priceType', JSON.stringify(priceType));
    if (priceType === 'pkg') {
      const parsedPrice = {
        thb: {
          kid: price.thb.kid ? parseFloat(price.thb.kid) : undefined,
          adult: parseFloat(price.thb.adult)
        },
        usd: {
          kid: price.usd.kid ? parseFloat(price.usd.kid) : undefined,
          adult: parseFloat(price.usd.adult)
        }
      };
      formData.append('price', JSON.stringify(parsedPrice));
      formData.append('isHalfPrice', isHalfPrice);
      if (isHalfPrice) {
        const parsedHalfPrice = {
          thb: {
            kid: halfPrice.thb.kid ? parseFloat(halfPrice.thb.kid) : undefined,
            adult: parseFloat(halfPrice.thb.adult)
          },
          usd: {
            kid: halfPrice.usd.kid ? parseFloat(halfPrice.usd.kid) : undefined,
            adult: parseFloat(halfPrice.usd.adult)
          }
        };
        formData.append('halfPrice', JSON.stringify(parsedHalfPrice));
      }
    } else if (priceType === 'boat') {
      const parsedSpeedBoatPrice = {
        thb: {
          kid: speedBoatPrice.thb.kid ? parseFloat(speedBoatPrice.thb.kid) : undefined,
          adult: parseFloat(speedBoatPrice.thb.adult)
        },
        usd: {
          kid: speedBoatPrice.usd.kid ? parseFloat(speedBoatPrice.usd.kid) : undefined,
          adult: parseFloat(speedBoatPrice.usd.adult)
        }
      };
      formData.append('speedBoatPrice', JSON.stringify(parsedSpeedBoatPrice));
      formData.append('isLongTailPrice', isLongTailPrice);
      if (isLongTailPrice) {
        const parsedLongTailPrice = {
          thb: {
            kid: longTailPrice.thb.kid ? parseFloat(longTailPrice.thb.kid) : undefined,
            adult: parseFloat(longTailPrice.thb.adult)
          },
          usd: {
            kid: longTailPrice.usd.kid ? parseFloat(longTailPrice.usd.kid) : undefined,
            adult: parseFloat(longTailPrice.usd.adult)
          }
        };
        formData.append('longTailPrice', JSON.stringify(parsedLongTailPrice));
      }
    }
    formData.append('isEntranceFees', enableEntranceFees);
    formData.append('popular', popular);
    if (enableEntranceFees) {
      formData.append(
        'entranceFees',
        JSON.stringify({
          thb: parseFloat(entranceFees.thb),
          usd: parseFloat(entranceFees.usd)
        })
      );
    }

    // Compress all uploaded images before sending to backend.
    const compressedImages = await compressImages(images, { maxSizeMB: 1, maxWidthOrHeight: 1920 });
    compressedImages.forEach((img) => formData.append('images', img));

    try {
      setLoader(true);
      const res = await fetch(`${BeURL}/create-package`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      const data = await res.json();
      setLoader(false);
      if (data.success) {
        alert(t('create-package.success'));
        window.location.href = "/admin/manage-packages";
      } else {
        setErrors(prev => ({ ...prev, form: data.message || t('create-package.failure') }));
      }
    } catch (error) {
      console.error('Error creating package:', error);
      setLoader(false);
      setErrors(prev => ({ ...prev, form: t('create-package.failure') }));
    }
  };

  return (
    <div className="mx-auto my-2 max-w-7xl">
      <h2 className="mb-4 text-center text-2xl font-extrabold tracking-tight text-[#1739bd]">{t('create-package.heading')}</h2>
      {errors.form && <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">{errors.form}</div>}
      <form onSubmit={handleSubmit} className="rounded-3xl border border-slate-200 bg-white p-4 shadow-[0_14px_34px_rgba(15,23,42,0.06)] sm:p-5">
        <div className="grid gap-3 sm:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3">
          {/* Titles */}
          {['en', 'fr', 'ts'].map(lang => (
            <div key={`title-${lang}`} className="w-full min-w-0">
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('create-package.title')} ({lang.toUpperCase()}) *</label>
              <input
                type="text"
                className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none ring-[#1739bd]/30 transition focus:border-[#1739bd] focus:ring"
                value={title[lang]}
                onChange={e => {
                  setTitle({ ...title, [lang]: e.target.value });
                  if (e.target.value.trim()) setErrors(prev => ({ ...prev, [`title_${lang}`]: '' }));
                }}
                onBlur={e => handleBlur(`title_${lang}`, e.target.value)}
                required
              />
              {errors[`title_${lang}`] && <small className="mt-1 block text-xs font-medium text-rose-600">{errors[`title_${lang}`]}</small>}
            </div>
          ))}

          {/* Descriptions */}
          {['en', 'fr', 'ts'].map(lang => (
            <div key={`description-${lang}`} className="w-full min-w-0">
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('create-package.description')} ({lang.toUpperCase()}) *</label>
              <textarea
                className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none ring-[#1739bd]/30 transition focus:border-[#1739bd] focus:ring"
                value={description[lang]}
                onChange={e => {
                  setDescription({ ...description, [lang]: e.target.value });
                  if (e.target.value.trim()) setErrors(prev => ({ ...prev, [`description_${lang}`]: '' }));
                }}
                onBlur={e => handleBlur(`description_${lang}`, e.target.value)}
                required
              ></textarea>
              {errors[`description_${lang}`] && <small className="mt-1 block text-xs font-medium text-rose-600">{errors[`description_${lang}`]}</small>}
            </div>
          ))}

          {/* Activities */}
          {['en', 'fr', 'ts'].map(lang => (
            <div key={`activities-${lang}`} className="w-full min-w-0">
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('create-package.activities')} ({lang.toUpperCase()}) *</label>
              <textarea
                className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none ring-[#1739bd]/30 transition focus:border-[#1739bd] focus:ring"
                placeholder={t('create-package.enterCommaSeparated')}
                value={activities[lang]}
                onChange={e => {
                  setActivities({ ...activities, [lang]: e.target.value });
                  if (e.target.value.trim()) setErrors(prev => ({ ...prev, [`activities_${lang}`]: '' }));
                }}
                onBlur={e => handleBlur(`activities_${lang}`, e.target.value)}
                required
              ></textarea>
              {errors[`activities_${lang}`] && <small className="mt-1 block text-xs font-medium text-rose-600">{errors[`activities_${lang}`]}</small>}
            </div>
          ))}

          {/* Inclusion (optional) */}
          {['en', 'fr', 'ts'].map(lang => (
            <div key={`inclusion-${lang}`} className="w-full min-w-0">
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('create-package.inclusion')} ({lang.toUpperCase()})</label>
              <textarea
                className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none ring-[#1739bd]/30 transition focus:border-[#1739bd] focus:ring"
                placeholder={t('create-package.enterCommaSeparated')}
                value={inclusion[lang]}
                onChange={e => setInclusion({ ...inclusion, [lang]: e.target.value })}
              ></textarea>
            </div>
          ))}

          {/* What To Bring (optional) */}
          {['en', 'fr', 'ts'].map(lang => (
            <div key={`whatToBring-${lang}`} className="w-full min-w-0">
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('create-package.whatToBring')} ({lang.toUpperCase()})</label>
              <textarea
                className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none ring-[#1739bd]/30 transition focus:border-[#1739bd] focus:ring"
                placeholder={t('create-package.enterCommaSeparated')}
                value={whatToBring[lang]}
                onChange={e => setWhatToBring({ ...whatToBring, [lang]: e.target.value })}
              ></textarea>
            </div>
          ))}

          {/* Destinations */}
          {['en', 'fr', 'ts'].map(lang => (
            <div key={`destinations-${lang}`} className="w-full min-w-0">
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('create-package.destinations')} ({lang.toUpperCase()}) *</label>
              <textarea
                className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none ring-[#1739bd]/30 transition focus:border-[#1739bd] focus:ring"
                placeholder={t('create-package.enterCommaSeparated')}
                value={destinations[lang]}
                onChange={e => {
                  setDestinations({ ...destinations, [lang]: e.target.value });
                  if (e.target.value.trim()) setErrors(prev => ({ ...prev, [`destinations_${lang}`]: '' }));
                }}
                onBlur={e => handleBlur(`destinations_${lang}`, e.target.value)}
                required
              ></textarea>
              {errors[`destinations_${lang}`] && <small className="mt-1 block text-xs font-medium text-rose-600">{errors[`destinations_${lang}`]}</small>}
            </div>
          ))}

          {/* Price Type Radio */}
          <div className="w-full mt-3 min-w-0">
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('create-package.priceType')}</label>
            <div className="flex flex-wrap gap-4">
              <label className="inline-flex items-center gap-2">
                <input
                  type="radio"
                  name="priceType"
                  value="pkg"
                  checked={priceType === "pkg"}
                  onChange={() => setPriceType("pkg")}
                /> {t('create-package.package')}
              </label>
              <label className="inline-flex items-center gap-2">
                <input
                  type="radio"
                  name="priceType"
                  value="boat"
                  checked={priceType === "boat"}
                  onChange={() => setPriceType("boat")}
                /> {t('create-package.boat')}
              </label>
            </div>
          </div>

          {/* Price Fields for Package */}
          {priceType === "pkg" && (
            <div className="mt-3 w-full min-w-0">
              <h4 className="text-sm font-bold uppercase tracking-[0.08em] text-slate-700">{t('create-package.packagePrice')}</h4>
              <div className="mt-3 grid gap-3 sm:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3">
                <div className="w-full min-w-0">
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('create-package.priceTHBAdult')} *</label>
                  <input
                    type="number"
                    className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none ring-[#1739bd]/30 transition focus:border-[#1739bd] focus:ring"
                    value={price.thb.adult}
                    onChange={e => {
                      setPrice({ ...price, thb: { ...price.thb, adult: e.target.value } });
                      if (e.target.value.trim()) setErrors(prev => ({ ...prev, 'price_pkg_thb_adult': '' }));
                    }}
                    onBlur={e => handleBlur('price_pkg_thb_adult', e.target.value)}
                    required
                  />
                  {errors['price_pkg_thb_adult'] && <small className="mt-1 block text-xs font-medium text-rose-600">{errors['price_pkg_thb_adult']}</small>}
                </div>
                <div className="w-full min-w-0">
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('create-package.priceUSDAdult')} *</label>
                  <input
                    type="number"
                    className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none ring-[#1739bd]/30 transition focus:border-[#1739bd] focus:ring"
                    value={price.usd.adult}
                    onChange={e => {
                      setPrice({ ...price, usd: { ...price.usd, adult: e.target.value } });
                      if (e.target.value.trim()) setErrors(prev => ({ ...prev, 'price_pkg_usd_adult': '' }));
                    }}
                    onBlur={e => handleBlur('price_pkg_usd_adult', e.target.value)}
                    required
                  />
                  {errors['price_pkg_usd_adult'] && <small className="mt-1 block text-xs font-medium text-rose-600">{errors['price_pkg_usd_adult']}</small>}
                </div>
                {/* Optional kid prices */}
                <div className="w-full min-w-0">
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('create-package.priceTHBKid')}</label>
                  <input
                    type="number"
                    className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none ring-[#1739bd]/30 transition focus:border-[#1739bd] focus:ring"
                    value={price.thb.kid}
                    onChange={e => setPrice({ ...price, thb: { ...price.thb, kid: e.target.value } })}
                  />
                </div>
                <div className="w-full min-w-0">
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('create-package.priceUSDKid')}</label>
                  <input
                    type="number"
                    className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none ring-[#1739bd]/30 transition focus:border-[#1739bd] focus:ring"
                    value={price.usd.kid}
                    onChange={e => setPrice({ ...price, usd: { ...price.usd, kid: e.target.value } })}
                  />
                </div>
              </div>
              {/* Enable Half Price for Package */}
              <div className="mt-2 flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300 text-[#1739bd] focus:ring-[#1739bd]"
                  id="halfPriceCheckbox"
                  checked={isHalfPrice}
                  onChange={() => setIsHalfPrice(!isHalfPrice)}
                />
                <label className="text-sm font-medium text-slate-700" htmlFor="halfPriceCheckbox">
                  {t('create-package.enableHalfPrice')}
                </label>
              </div>
              {isHalfPrice && (
                <div className="mt-2 grid gap-3 sm:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3">
                  <div className="w-full min-w-0">
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('create-package.halfPriceTHBAdult')} *</label>
                    <input
                      type="number"
                      className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none ring-[#1739bd]/30 transition focus:border-[#1739bd] focus:ring"
                      value={halfPrice.thb.adult}
                      onChange={e => {
                        setHalfPrice({ ...halfPrice, thb: { ...halfPrice.thb, adult: e.target.value } });
                        if (e.target.value.trim()) setErrors(prev => ({ ...prev, 'halfPrice_pkg_thb_adult': '' }));
                      }}
                      onBlur={e => handleBlur('halfPrice_pkg_thb_adult', e.target.value)}
                      required
                    />
                    {errors['halfPrice_pkg_thb_adult'] && <small className="mt-1 block text-xs font-medium text-rose-600">{errors['halfPrice_pkg_thb_adult']}</small>}
                  </div>
                  <div className="w-full min-w-0">
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('create-package.halfPriceUSDAdult')} *</label>
                    <input
                      type="number"
                      className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none ring-[#1739bd]/30 transition focus:border-[#1739bd] focus:ring"
                      value={halfPrice.usd.adult}
                      onChange={e => {
                        setHalfPrice({ ...halfPrice, usd: { ...halfPrice.usd, adult: e.target.value } });
                        if (e.target.value.trim()) setErrors(prev => ({ ...prev, 'halfPrice_pkg_usd_adult': '' }));
                      }}
                      onBlur={e => handleBlur('halfPrice_pkg_usd_adult', e.target.value)}
                      required
                    />
                    {errors['halfPrice_pkg_usd_adult'] && <small className="mt-1 block text-xs font-medium text-rose-600">{errors['halfPrice_pkg_usd_adult']}</small>}
                  </div>
                  {/* Optional kid prices for half price */}
                  <div className="w-full min-w-0">
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('create-package.halfPriceTHBKid')}</label>
                    <input
                      type="number"
                      className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none ring-[#1739bd]/30 transition focus:border-[#1739bd] focus:ring"
                      value={halfPrice.thb.kid}
                      onChange={e => setHalfPrice({ ...halfPrice, thb: { ...halfPrice.thb, kid: e.target.value } })}
                    />
                  </div>
                  <div className="w-full min-w-0">
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('create-package.halfPriceUSDKid')}</label>
                    <input
                      type="number"
                      className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none ring-[#1739bd]/30 transition focus:border-[#1739bd] focus:ring"
                      value={halfPrice.usd.kid}
                      onChange={e => setHalfPrice({ ...halfPrice, usd: { ...halfPrice.usd, kid: e.target.value } })}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Price Fields for Boat */}
          {priceType === "boat" && (
            <div className="mt-3 w-full min-w-0">
              <h4 className="text-sm font-bold uppercase tracking-[0.08em] text-slate-700">{t('create-package.speedBoatPrice')}</h4>
              <div className="mt-3 grid gap-3 sm:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3">
                <div className="w-full min-w-0">
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('create-package.speedBoatPriceTHBAdult')} *</label>
                  <input
                    type="number"
                    className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none ring-[#1739bd]/30 transition focus:border-[#1739bd] focus:ring"
                    value={speedBoatPrice.thb.adult}
                    onChange={e => {
                      setSpeedBoatPrice({ ...speedBoatPrice, thb: { ...speedBoatPrice.thb, adult: e.target.value } });
                      if (e.target.value.trim()) setErrors(prev => ({ ...prev, 'speedBoat_thb_adult': '' }));
                    }}
                    onBlur={e => handleBlur('speedBoat_thb_adult', e.target.value)}
                    required
                  />
                  {errors['speedBoat_thb_adult'] && <small className="mt-1 block text-xs font-medium text-rose-600">{errors['speedBoat_thb_adult']}</small>}
                </div>
                <div className="w-full min-w-0">
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('create-package.speedBoatPriceUSDAdult')} *</label>
                  <input
                    type="number"
                    className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none ring-[#1739bd]/30 transition focus:border-[#1739bd] focus:ring"
                    value={speedBoatPrice.usd.adult}
                    onChange={e => {
                      setSpeedBoatPrice({ ...speedBoatPrice, usd: { ...speedBoatPrice.usd, adult: e.target.value } });
                      if (e.target.value.trim()) setErrors(prev => ({ ...prev, 'speedBoat_usd_adult': '' }));
                    }}
                    onBlur={e => handleBlur('speedBoat_usd_adult', e.target.value)}
                    required
                  />
                  {errors['speedBoat_usd_adult'] && <small className="mt-1 block text-xs font-medium text-rose-600">{errors['speedBoat_usd_adult']}</small>}
                </div>
                {/* Optional kid prices */}
                <div className="w-full min-w-0">
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('create-package.speedBoatPriceTHBKid')}</label>
                  <input
                    type="number"
                    className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none ring-[#1739bd]/30 transition focus:border-[#1739bd] focus:ring"
                    value={speedBoatPrice.thb.kid}
                    onChange={e => setSpeedBoatPrice({ ...speedBoatPrice, thb: { ...speedBoatPrice.thb, kid: e.target.value } })}
                  />
                </div>
                <div className="w-full min-w-0">
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('create-package.speedBoatPriceUSDKid')}</label>
                  <input
                    type="number"
                    className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none ring-[#1739bd]/30 transition focus:border-[#1739bd] focus:ring"
                    value={speedBoatPrice.usd.kid}
                    onChange={e => setSpeedBoatPrice({ ...speedBoatPrice, usd: { ...speedBoatPrice.usd, kid: e.target.value } })}
                  />
                </div>
              </div>
              {/* Enable Longtail Price for Boat */}
              <div className="mt-2 flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300 text-[#1739bd] focus:ring-[#1739bd]"
                  id="longTailPriceCheckbox"
                  checked={isLongTailPrice}
                  onChange={() => setIsLongTailPrice(!isLongTailPrice)}
                />
                <label className="text-sm font-medium text-slate-700" htmlFor="longTailPriceCheckbox">
                  {t('create-package.enableLongTailPrice')}
                </label>
              </div>
              {isLongTailPrice && (
                <div className="mt-2 grid gap-3 sm:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3">
                  <div className="w-full min-w-0">
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('create-package.longTailPriceTHBAdult')} *</label>
                    <input
                      type="number"
                      className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none ring-[#1739bd]/30 transition focus:border-[#1739bd] focus:ring"
                      value={longTailPrice.thb.adult}
                      onChange={e => {
                        setLongTailPrice({ ...longTailPrice, thb: { ...longTailPrice.thb, adult: e.target.value } });
                        if (e.target.value.trim()) setErrors(prev => ({ ...prev, 'longTail_thb_adult': '' }));
                      }}
                      onBlur={e => handleBlur('longTail_thb_adult', e.target.value)}
                      required
                    />
                    {errors['longTail_thb_adult'] && <small className="mt-1 block text-xs font-medium text-rose-600">{errors['longTail_thb_adult']}</small>}
                  </div>
                  <div className="w-full min-w-0">
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('create-package.longTailPriceUSDAdult')} *</label>
                    <input
                      type="number"
                      className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none ring-[#1739bd]/30 transition focus:border-[#1739bd] focus:ring"
                      value={longTailPrice.usd.adult}
                      onChange={e => {
                        setLongTailPrice({ ...longTailPrice, usd: { ...longTailPrice.usd, adult: e.target.value } });
                        if (e.target.value.trim()) setErrors(prev => ({ ...prev, 'longTail_usd_adult': '' }));
                      }}
                      onBlur={e => handleBlur('longTail_usd_adult', e.target.value)}
                      required
                    />
                    {errors['longTail_usd_adult'] && <small className="mt-1 block text-xs font-medium text-rose-600">{errors['longTail_usd_adult']}</small>}
                  </div>
                  {/* Optional kid prices */}
                  <div className="w-full min-w-0">
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('create-package.longTailPriceTHBKid')}</label>
                    <input
                      type="number"
                      className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none ring-[#1739bd]/30 transition focus:border-[#1739bd] focus:ring"
                      value={longTailPrice.thb.kid}
                      onChange={e => setLongTailPrice({ ...longTailPrice, thb: { ...longTailPrice.thb, kid: e.target.value } })}
                    />
                  </div>
                  <div className="w-full min-w-0">
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('create-package.longTailPriceUSDKid')}</label>
                    <input
                      type="number"
                      className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none ring-[#1739bd]/30 transition focus:border-[#1739bd] focus:ring"
                      value={longTailPrice.usd.kid}
                      onChange={e => setLongTailPrice({ ...longTailPrice, usd: { ...longTailPrice.usd, kid: e.target.value } })}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Entrance Fees */}
          <div className="w-full mt-3 min-w-0">
            <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300 text-[#1739bd] focus:ring-[#1739bd]"
                id="entranceFeesCheckbox"
                checked={enableEntranceFees}
                onChange={() => setEnableEntranceFees(!enableEntranceFees)}
              />
              <label className="text-sm font-medium text-slate-700" htmlFor="entranceFeesCheckbox">
                {t('create-package.enableEntranceFees')}
              </label>
            </div>
            {enableEntranceFees && (
              <div className="mt-2 grid gap-3 sm:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3">
                <div className="w-full min-w-0">
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('update-package.entranceFees')} (THB) *</label>
                  <input
                    type="number"
                    className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none ring-[#1739bd]/30 transition focus:border-[#1739bd] focus:ring"
                    value={entranceFees.thb}
                    onChange={e => setEntranceFees({ ...entranceFees, thb: e.target.value })}
                    onBlur={e => handleBlur('entranceFees_thb', e.target.value)}
                    required
                  />
                  {errors['entranceFees_thb'] && <small className="mt-1 block text-xs font-medium text-rose-600">{errors['entranceFees_thb']}</small>}
                </div>
                <div className="w-full min-w-0">
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('update-package.entranceFees')} (USD) *</label>
                  <input
                    type="number"
                    className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm outline-none ring-[#1739bd]/30 transition focus:border-[#1739bd] focus:ring"
                    value={entranceFees.usd}
                    onChange={e => setEntranceFees({ ...entranceFees, usd: e.target.value })}
                    onBlur={e => handleBlur('entranceFees_usd', e.target.value)}
                    required
                  />
                  {errors['entranceFees_usd'] && <small className="mt-1 block text-xs font-medium text-rose-600">{errors['entranceFees_usd']}</small>}
                </div>
              </div>
            )}
          </div>

          <div className="my-3 flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-slate-300 text-[#1739bd] focus:ring-[#1739bd]"
              id="popular"
              checked={popular}
              onChange={() => setPopular(!popular)}
            />
            <label className="text-sm font-medium text-slate-700" htmlFor="popular">
              {t('create-package.popular')}
            </label>
          </div>

          {/* Image Upload Fields */}
          <div className="w-full mt-3 min-w-0">
            <label className="mb-1.5 block py-2 text-center text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('create-package.images')} *</label>
            <div className="grid w-full gap-3 grid-cols-2 lg:grid-cols-4">
              {images.map((img, i) => (
                <div key={i} className="relative aspect-square w-full overflow-hidden rounded-xl">
                  <img
                    src={imagePreviews[i]}
                    alt={`Upload Preview ${i + 1}`}
                    className="h-full w-full rounded-xl border border-slate-200 object-cover"
                  />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={e => handleImageChange(e, i)}
                    className="absolute inset-0 h-full w-full opacity-0"
                    required
                  />
                </div>
              ))}
              {errors['images'] && <small className="mt-1 block text-xs font-medium text-rose-600">{errors['images']}</small>}
            </div>
          </div>
        </div>

        <div className="mt-6 text-center">
          <button type="submit" className="inline-flex items-center rounded-2xl bg-[#1739bd] px-5 py-2.5 text-sm font-semibold text-white shadow-lg transition hover:bg-indigo-700">
            {t('create-package.submit')}
          </button>
        </div>
      </form>
    </div>
  );
}

export default CreatePackage;


